/**
 * On-screen touch controls for mobile/tablet: a virtual joystick (steer) on the
 * left and action buttons on the right, feeding the same Command as the
 * keyboard. Additive — only shown on touch devices (or with ?touch=1). The
 * battle merges this with the keyboard command, so either input works.
 */
import Phaser from 'phaser';
import type { Command } from '../game/command.ts';
import { WORLD } from '../world.ts';

/** Whether on-screen controls should be shown for this device/session. */
export function touchEnabled(): boolean {
  if (new URLSearchParams(location.search).has('touch')) return true;
  return (
    'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0
  );
}

interface Btn {
  zone: Phaser.GameObjects.Arc;
  down: boolean;
  /** Set on press; consumed (edge-triggered) by command(). */
  fired: boolean;
}

export class TouchControls {
  private steer = { x: 0, y: 0 };
  private joyPointerId = -1;
  private readonly joyBaseX = 150;
  private readonly joyBaseY = WORLD.height - 150;
  private readonly joyRadius = 80;
  private thumb!: Phaser.GameObjects.Arc;

  private dash!: Btn;
  private extreme!: Btn;
  private center!: Btn;
  private special!: Btn;

  constructor(private readonly scene: Phaser.Scene) {
    scene.input.addPointer(3); // allow simultaneous joystick + button touches

    const g = scene.add.graphics().setDepth(80).setScrollFactor(0);
    g.fillStyle(0xffffff, 0.08);
    g.fillCircle(this.joyBaseX, this.joyBaseY, this.joyRadius);
    g.lineStyle(3, 0xffffff, 0.25);
    g.strokeCircle(this.joyBaseX, this.joyBaseY, this.joyRadius);
    this.thumb = scene.add
      .circle(this.joyBaseX, this.joyBaseY, 34, 0xffffff, 0.22)
      .setDepth(81)
      .setScrollFactor(0);

    // Action buttons (bottom-right cluster).
    const rx = WORLD.width - 110;
    const ry = WORLD.height - 150;
    this.dash = this.makeButton(rx, ry, 46, 0xff5a2a, 'STRIKE');
    this.extreme = this.makeButton(rx - 130, ry - 40, 40, 0xffd23b, 'EXT');
    this.special = this.makeButton(rx - 110, ry + 90, 40, 0x5ad6ff, 'SP');
    this.center = this.makeButton(rx + 6, ry + 96, 36, 0xb0c4de, 'CEN');

    scene.input.on('pointerdown', this.onPointer, this);
    scene.input.on('pointermove', this.onPointer, this);
    scene.input.on('pointerup', this.onPointerUp, this);
  }

  private makeButton(x: number, y: number, r: number, color: number, label: string): Btn {
    const zone = this.scene.add
      .circle(x, y, r, color, 0.22)
      .setStrokeStyle(3, color, 0.6)
      .setDepth(80)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    this.scene.add
      .text(x, y, label, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '15px',
        fontStyle: 'bold',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setDepth(81)
      .setScrollFactor(0);
    const btn: Btn = { zone, down: false, fired: false };
    zone.on('pointerdown', () => {
      btn.down = true;
      btn.fired = true;
      zone.setFillStyle(color, 0.5);
    });
    const release = () => {
      btn.down = false;
      zone.setFillStyle(color, 0.22);
    };
    zone.on('pointerup', release);
    zone.on('pointerout', release);
    return btn;
  }

  /** Joystick tracking — ignores pointers that are over the (right-side) buttons. */
  private onPointer(pointer: Phaser.Input.Pointer): void {
    if (!pointer.isDown) return;
    // Only the left half of the screen drives the joystick.
    if (this.joyPointerId === -1 && pointer.x > WORLD.width / 2) return;
    if (this.joyPointerId !== -1 && pointer.id !== this.joyPointerId) return;
    this.joyPointerId = pointer.id;
    const dx = pointer.x - this.joyBaseX;
    const dy = pointer.y - this.joyBaseY;
    const len = Math.hypot(dx, dy) || 1;
    const clamped = Math.min(len, this.joyRadius);
    const nx = (dx / len) * clamped;
    const ny = (dy / len) * clamped;
    this.thumb.setPosition(this.joyBaseX + nx, this.joyBaseY + ny);
    // Steer magnitude 0..1; small dead zone.
    const mag = clamped / this.joyRadius;
    this.steer = mag < 0.15 ? { x: 0, y: 0 } : { x: dx / len, y: dy / len };
  }

  private onPointerUp(pointer: Phaser.Input.Pointer): void {
    if (pointer.id === this.joyPointerId) {
      this.joyPointerId = -1;
      this.steer = { x: 0, y: 0 };
      this.thumb.setPosition(this.joyBaseX, this.joyBaseY);
    }
  }

  /** The current command from touch input (edge flags consumed each call). */
  command(): Command {
    const cmd: Command = {
      steer: { x: this.steer.x, y: this.steer.y },
      dash: this.dash.fired,
      extremeDash: this.extreme.fired,
      holdCenter: this.center.down,
      special: this.special.fired,
    };
    this.dash.fired = false;
    this.extreme.fired = false;
    this.special.fired = false;
    return cmd;
  }
}
